import { Card, Checkbox, Input, Tabs, TabsProps, Tag } from 'antd';
import React from 'react';

export interface SelectableItem {
  label: string;
  value: string;
}

export interface CustomSelectorTabInterface<T> {
  label: string;
  selected: T[];
  setSelected: (selected: T[]) => void;
  fetchSearch: (search: string) => Promise<T[]>;
  getLabel: (item: T) => string;
}

export interface CustomSelectorProps<T> {
  tabs: CustomSelectorTabInterface<T>[];
}

const CustomSelector = <T extends string | number | object>({ tabs }: CustomSelectorProps<T>) => {
  const [selectedTab, setSelectedTab] = React.useState<string>(tabs[0].label);
  const [tabStates, setTabStates] = React.useState<
    Record<string, { searchKeyword: string; options: T[] }>
  >({});

  const updateTabState = (
    tabLabel: string,
    updates: Partial<{ searchKeyword: string; options: T[] }>
  ) => {
    setTabStates(prev => ({
      ...prev,
      [tabLabel]: { ...prev[tabLabel], ...updates },
    }));
  };

  const tabItems: TabsProps['items'] = tabs.map(tab => {
    const tabState = tabStates[tab.label] || { searchKeyword: '', options: [] };

    return {
      key: tab.label,
      label: tab.label,
      children: (
        <div style={{ display: 'flex', flexDirection: 'row', height: '150px', gap: 5 }}>
          <Card
            style={{
              width: '20%',
              height: '150px',
              border: '1px solid black',
              overflowY: 'auto',
            }}
          >
            {tab.selected.map(item => (
              <Tag key={tab.getLabel(item)}>{tab.getLabel(item)}</Tag>
            ))}
          </Card>
          <div style={{ width: '100%', height: '100%', border: '1px solid black', padding: 10 }}>
            <div style={{ height: '20%', marginBottom: 10 }}>
              <Input
                placeholder={`Search ${tab.label}`}
                value={tabState.searchKeyword}
                onChange={e => updateTabState(tab.label, { searchKeyword: e.target.value })}
              />
            </div>
            <div style={{ flex: 1, height: 'calc(100% - 20% - 10px)', overflowY: 'auto' }}>
              <Checkbox.Group
                options={tabState.options.map(item => ({
                  label: tab.getLabel(item) || '',
                  value: tab.getLabel(item) || '',
                }))}
                value={tab.selected.map(item => tab.getLabel(item))}
                onChange={(checkedValues: string[]) => {
                  const selected = checkedValues.map(value =>
                    tabState.options.find(item => tab.getLabel(item) === value)
                  );
                  tab.setSelected(selected.filter(item => item !== undefined) as T[]);
                }}
              />
            </div>
          </div>
        </div>
      ),
    };
  });

  React.useEffect(() => {
    const currentTab = tabs.find(tab => tab.label === selectedTab);
    if (currentTab) {
      const tabState = tabStates[selectedTab];
      if (tabState) {
        currentTab.fetchSearch(tabState.searchKeyword).then(res => {
          updateTabState(selectedTab, { options: res });
        });
      }
    }
  }, [tabStates[selectedTab]?.searchKeyword, selectedTab]);

  React.useEffect(() => {
    const initialState: Record<string, { searchKeyword: string; options: T[] }> = {};
    tabs.forEach(tab => {
      initialState[tab.label] = { searchKeyword: '', options: [] };
    });
    setTabStates(initialState);
  }, []);

  return (
    <div style={{ height: '100%' }}>
      <Tabs
        defaultActiveKey={selectedTab}
        type="card"
        onChange={setSelectedTab}
        style={{ height: '100%' }}
        items={tabItems}
      />
    </div>
  );
};

export default CustomSelector;
